import styled from "@emotion/styled";
import { BackgroundThumbnail, SimpleMenu } from "@kaltura/mediaspace-shared-ui";
import { Button, MenuItem } from "@kaltura/ds-react-components";
import { Edit24Icon } from "@kaltura/ds-react-icons";
import { ComponentType, ReactNode } from "react";
import { BaseUploadData } from "../InPlaceUpload";

export interface ThumbnailAction {
    label: string;
    callback: () => void;
}

interface UploadThumbnailProps<DataT extends BaseUploadData = BaseUploadData> {
    data?: DataT;
    thumbnailPlaceholder?: ReactNode;
    previewComponent?: ComponentType<{ data: DataT }>;
    buttonLabel?: string;
    buttonAriaLabel?: string;
    actions?: ThumbnailAction[];
    icon?: ReactNode;
    onMenuOpen?: () => void;
}

const StyledContainer = styled("div")({
    height: "100%",
    display: "flex",
    position: "relative",
});

const StyledThumbnail = styled(BackgroundThumbnail)(({ theme }) => ({
    borderRadius: theme.kaltura.shape.roundness2,
}));

const StyledSimpleMenu = styled(SimpleMenu)(({ theme }) => ({
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
}));

const StyledIconContainer = styled("div")(({ theme }) => ({
    position: "absolute",
    bottom: theme.spacing(1),
    left: theme.spacing(1),
    color: theme.kaltura.palette.primary.contrastText,
}));

/**
 * in place uploader thumbnail with edit menu
 */
export const UploadThumbnail = <DataT extends BaseUploadData>(props: UploadThumbnailProps<DataT>) => {
    const {
        data,
        thumbnailPlaceholder,
        previewComponent: PreviewComponent,
        buttonLabel,
        buttonAriaLabel,
        actions,
        icon,
        onMenuOpen,
    } = props;

    return (
        <StyledContainer>
            {PreviewComponent && data ? (
                <PreviewComponent data={data} />
            ) : data ? (
                <StyledThumbnail thumbnailUrl={data.thumbnailUrl}></StyledThumbnail>
            ) : (
                thumbnailPlaceholder
            )}

            {icon && <StyledIconContainer>{icon}</StyledIconContainer>}

            {actions !== undefined && (
                <StyledSimpleMenu
                    id={"thumbnailActions"}
                    button={
                        <Button color={"translucent"} startIcon={<Edit24Icon />} aria-label={buttonAriaLabel}>
                            {buttonLabel}
                        </Button>
                    }
                    onOpen={onMenuOpen}
                >
                    {actions.map((action, index) => (
                        <MenuItem key={index} onClick={action.callback}>
                            {action.label}
                        </MenuItem>
                    ))}
                </StyledSimpleMenu>
            )}
        </StyledContainer>
    );
};
